'use client';

import { Box, Button, Theme, Typography, useTheme } from '@mui/material';
import { ReactElement, ReactNode, useId } from 'react';
import { StickyActionBar } from '../../StickyActionBar/StickyActionBar';
import { PHONE_PAGE_PADDING_BOTTOM, PHONE_PAGE_PADDING_X } from './requestChooseScreenLayout';
import type { RequestChooseScreenCopy, RequestPrimaryAction } from './requestChooseScreenCopy';

export type RequestActionAreaProps = {
  layout: 'desktop' | 'phone';
  primaryAction: RequestPrimaryAction;
  /** The chosen option's full name, or undefined while nothing is chosen. */
  selectedName: string | undefined;
  copy: Pick<
    RequestChooseScreenCopy,
    'primaryLabels' | 'cancel' | 'selectedPrefix' | 'nothingChosenPrompt'
  >;
  onPrimary: () => void;
  onCancel: () => void;
};

type LineProps = { id: string; prefix: string; name: string };

// The whole name, never clamped: this is where a customer reads the full name of a clamped card.
function ChosenLine({ id, prefix, name }: Readonly<LineProps>): ReactElement {
  return (
    <Typography id={id} variant="body2" component="p" sx={{ margin: 0, minWidth: 0 }}>
      <span>{prefix}</span>{' '}
      <Box
        component="strong"
        sx={(theme: Theme) => ({
          fontWeight: theme.semantic.fontWeight.semibold,
          overflowWrap: 'anywhere',
        })}
      >
        {name}
      </Box>
    </Typography>
  );
}

function Prompt({ id, children }: Readonly<{ id: string; children: ReactNode }>): ReactElement {
  return (
    <Typography
      id={id}
      variant="body2"
      component="p"
      sx={(theme: Theme) => ({ margin: 0, color: theme.palette.text.tertiary })}
    >
      {children}
    </Typography>
  );
}

// The board draws these buttons 44px high; the theme's default is 48px.
const actionButtonSx = (theme: Theme) => ({ minHeight: theme.requestActions.buttonHeight });

// StickyActionBar fades into transparent at its top, so page content shows through behind a line
// of text placed there. This panel gives the line and the primary a solid surface instead, as the
// board's bar is: it cancels the bar's own padding at the top and the sides, and puts it back
// inside, so nothing moves. A hairline marks the top edge where the board draws a shadow.
function BarPanel({ children }: Readonly<{ children: ReactNode }>): ReactElement {
  return (
    <Box
      sx={(theme: Theme) => {
        const inset = theme.semantic.spacing[PHONE_PAGE_PADDING_X];
        return {
          display: 'flex',
          flexDirection: 'column',
          gap: theme.semantic.spacing.sm,
          marginTop: `calc(-1 * ${theme.semantic.spacing.base})`,
          marginInline: `calc(-1 * ${inset})`,
          paddingTop: theme.semantic.spacing.base,
          paddingInline: inset,
          backgroundColor: theme.palette.background.default,
          borderTop: `${theme.semantic.spacing['4xs']} solid ${theme.palette.divider}`,
        };
      }}
    >
      {children}
    </Box>
  );
}

type ButtonsProps = Pick<RequestActionAreaProps, 'primaryAction' | 'onPrimary' | 'onCancel'> & {
  copy: RequestActionAreaProps['copy'];
  describedBy: string | undefined;
  fullWidth: boolean;
};

function PrimaryButton({
  primaryAction,
  onPrimary,
  copy,
  describedBy,
  fullWidth,
}: Readonly<Omit<ButtonsProps, 'onCancel'>>): ReactElement {
  return (
    <Button
      variant="contained"
      sx={actionButtonSx}
      fullWidth={fullWidth}
      data-action="request-primary"
      aria-describedby={describedBy}
      disabled={primaryAction === 'none'}
      onClick={onPrimary}
    >
      {copy.primaryLabels[primaryAction]}
    </Button>
  );
}

function CancelButton({
  onCancel,
  label,
  fullWidth,
}: Readonly<{ onCancel: () => void; label: string; fullWidth: boolean }>): ReactElement {
  return (
    <Button
      variant="outlined"
      sx={actionButtonSx}
      fullWidth={fullWidth}
      data-action="request-cancel"
      onClick={onCancel}
    >
      {label}
    </Button>
  );
}

// Desktop (D7): the chosen name on the left; Cancel and the primary together on the right.
function DesktopActions({
  primaryAction,
  selectedName,
  copy,
  onPrimary,
  onCancel,
}: Readonly<Omit<RequestActionAreaProps, 'layout'>>): ReactElement {
  const lineId = useId();
  return (
    <Box
      sx={(theme: Theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: theme.semantic.spacing.sm,
      })}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {selectedName && (
          <ChosenLine id={lineId} prefix={copy.selectedPrefix} name={selectedName} />
        )}
      </Box>
      <CancelButton onCancel={onCancel} label={copy.cancel} fullWidth={false} />
      <PrimaryButton
        primaryAction={primaryAction}
        onPrimary={onPrimary}
        copy={copy}
        describedBy={selectedName ? lineId : undefined}
        fullWidth={false}
      />
    </Box>
  );
}

// Phone (618 L1a, amending 617 Q3d): the sticky bar holds the chosen name (or a prompt while nothing
// is chosen), a full-width primary, then a full-width Cancel under it, the same height either way.
// Nothing follows the bar: the bar sits in the page's flow at the end, so the page's own height
// already reserves the bar's, and the last row scrolls clear of it.
function PhoneActions({
  primaryAction,
  selectedName,
  copy,
  onPrimary,
  onCancel,
}: Readonly<Omit<RequestActionAreaProps, 'layout'>>): ReactElement {
  const lineId = useId();
  const { spacing } = useTheme().semantic;
  return (
    <StickyActionBar
      // The page pads its content on a phone (requestChooseScreenLayout.ts). The bar negates that
      // padding to reach the screen's edges, and insets its content by the same amount, so the
      // button lines up with the page's content column.
      parentPaddingX={spacing[PHONE_PAGE_PADDING_X]}
      parentPaddingBottom={spacing[PHONE_PAGE_PADDING_BOTTOM]}
      contentInset={spacing[PHONE_PAGE_PADDING_X]}
    >
      <BarPanel>
        {selectedName ? (
          <ChosenLine id={lineId} prefix={copy.selectedPrefix} name={selectedName} />
        ) : (
          <Prompt id={lineId}>{copy.nothingChosenPrompt}</Prompt>
        )}
        <PrimaryButton
          primaryAction={primaryAction}
          onPrimary={onPrimary}
          copy={copy}
          describedBy={lineId}
          fullWidth
        />
        <CancelButton onCancel={onCancel} label={copy.cancel} fullWidth />
      </BarPanel>
    </StickyActionBar>
  );
}

/**
 * The action area: on a desktop a row (chosen name, Cancel, primary), on a phone the sticky bar
 * (chosen name, primary, Cancel). The primary is disabled when `primaryAction` is `none`, and its
 * label follows the action. It is described by the chosen name, so a screen reader says what it acts on.
 */
export function RequestActionArea({
  layout,
  ...props
}: Readonly<RequestActionAreaProps>): ReactElement {
  return layout === 'phone' ? <PhoneActions {...props} /> : <DesktopActions {...props} />;
}
